import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes, faCopy, faX } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './ShareButton.css';
import Alert from '../Alert/Alert';

const API_BASE_URL = 'https://api.minymol.com';

const ShareButton = ({ title, imageUrl, redirectUrl, minimal = true, isCatalogo = false }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [privateUrl, setPrivateUrl] = useState('');
  const [visible, setVisible] = useState(false);
  const [alert, setAlert] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setAlert({ type: 'success', message: 'Enlace copiado al portapapeles' });
    } catch (err) {
      setAlert({ type: 'error', message: 'No se pudo copiar el enlace' });
    }
    
    // Usar setTimeout para asegurar que el comando se procese antes de remover
    setTimeout(() => {
      try {
        if (textArea.parentNode === document.body) {
          document.body.removeChild(textArea);
        }
      } catch (e) {
        console.error('Error al limpiar textarea:', e);
      }
    }, 100);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setAlert({ type: 'success', message: 'Enlace copiado al portapapeles' });
    } catch (err) {
      fallbackCopy(text);
    }
  };

  const generateShareUrl = async () => {
    try {
      const usuario = JSON.parse(localStorage.getItem('usuario'));
      const providerId = isCatalogo
        ? redirectUrl?.split('/catalog/')[1] || usuario?.proveedorInfo?.id
        : null;

      if (!title || !imageUrl || (isCatalogo && !providerId)) {
        throw new Error('Faltan datos obligatorios');
      }

      // Verificar configuraciones de visibilidad si es catálogo
      let mostrarPublico = true;
      let mostrarPrivado = true;
      
      if (isCatalogo && usuario?.proveedorInfo) {
        mostrarPublico = usuario.proveedorInfo.mostrarPublico !== false;
        mostrarPrivado = usuario.proveedorInfo.mostrarPrivado !== false;
      }

      const headers = {
        'Content-Type': 'application/json',
      };

      const fetchShare = async (redirectUrl) => {
        const res = await fetch(`${API_BASE_URL}/share`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ title, imageUrl, redirectUrl }),
        });

        const json = await res.json();
        if (!res.ok || !json?.shareUrl) {
          throw new Error(json?.message || 'Error al crear enlace');
        }

        return json.shareUrl;
      };

      if (isCatalogo) {
        const publicRedirect = `https://minymol.com/catalog/${providerId}`;
        const privateRedirect = `https://ovicatalogos.com/?providerId=${providerId}`;

        // Solo generar enlaces para catálogos habilitados
        const promises = [];
        if (mostrarPublico) {
          promises.push(fetchShare(publicRedirect));
        } else {
          promises.push(Promise.resolve(''));
        }
        
        if (mostrarPrivado) {
          promises.push(fetchShare(privateRedirect));
        } else {
          promises.push(Promise.resolve(''));
        }

        const [publicLink, privateLink] = await Promise.all(promises);

        setPublicUrl(publicLink);
        setPrivateUrl(privateLink);
        setVisible(true);
      } else {
        const shareLink = await fetchShare(redirectUrl);
        setShareUrl(shareLink);
        if (minimal) setVisible(true);
        else window.open(`https://wa.me/?text=${encodeURIComponent(shareLink)}`, '_blank');
      }
    } catch (err) {
      console.error('❌ Error al compartir:', err);
      setAlert({ type: 'error', message: 'No se pudo generar el enlace para compartir.' });
    }
  };



  const closeModal = () => setVisible(false);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('container-share')) closeModal();
  };

  useEffect(() => {
    if (visible) {
      const scrollY = window.scrollY;
      setScrollPosition(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
      document.documentElement.classList.add('modal-open');
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.documentElement.classList.remove('modal-open');
      window.scrollTo(0, scrollPosition);
    }
  }, [visible, scrollPosition]);

  return (
    <>
      {minimal ? (
        <div className='cat-share-cont'>
          <button onClick={generateShareUrl}>
            <FontAwesomeIcon icon={faShareNodes} />
          </button>
        </div>
      ) : (
        <button className='btn-share' onClick={generateShareUrl}>
          <FontAwesomeIcon icon={faShareNodes} className='icon-share' />
          Compartir
        </button>
      )}

      {visible && (
        <div className='container-share' onClick={handleOverlayClick}>
          <div className='app-share-cont'>
            <button className='close-btn-share' onClick={closeModal}>
              <FontAwesomeIcon icon={faX} />
            </button>
            <h1>{isCatalogo ? 'Enlaces' : 'Compartir en'}</h1>
            <div className='line'></div>

            {isCatalogo ? (
              <div className='catalog-links'>
                {(() => {
                  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
                  const mostrarPublico = usuario?.proveedorInfo?.mostrarPublico !== false;
                  const mostrarPrivado = usuario?.proveedorInfo?.mostrarPrivado !== false;

                  return (
                    <>
                      {mostrarPublico && (
                        <div className='catalog-block'>
                          <p><strong>Catálogo público:</strong></p>
                          <div className='catolog-link'>
                            <input value={publicUrl} readOnly />
                            <button onClick={() => handleCopy(publicUrl)}>
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                          </div>
                        </div>
                      )}
                      {mostrarPrivado && (
                        <div className='catalog-block'>
                          <p><strong>Catálogo privado:</strong></p>
                          <div className='catolog-link'>
                            <input value={privateUrl} readOnly />
                            <button onClick={() => handleCopy(privateUrl)}>
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                          </div>
                        </div>
                      )}
                      {!mostrarPublico && !mostrarPrivado && (
                        <div className='catalog-block'>
                          <p style={{ textAlign: 'center', color: '#666' }}>
                            No hay catálogos disponibles para compartir
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className='share-buttons'>
                <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank')} className='share-btn whatsapp'>
                  <FontAwesomeIcon icon={faWhatsapp} className='icon-share' />
                  WhatsApp
                </button>
                <button onClick={() => handleCopy(shareUrl)} className='share-btn'>
                  <FontAwesomeIcon icon={faCopy} className='icon-share' />
                  Copiar enlace
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ShareButton;
